import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Form, Button, Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle, faChartBar, faSortAmountDown, faSortAmountUp } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "../styles/style.scss";

function UserSettings() {
  const { id } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const [isMetric, setIsMetric] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [urlName, setUrlName] = useState("");
  const [recipeUsageStats, setRecipeUsageStats] = useState(null);
  const [usageSortOrder, setUsageSortOrder] = useState("desc");
  const [showUsageStats, setShowUsageStats] = useState(false);
  const [courseFilter, setCourseFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [minUsageFilter, setMinUsageFilter] = useState("");
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Google Drive state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleSaveChanges = () => {
    setEditMode(!editMode);
  
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser/public-display`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        displayName: displayName,
        urlName: urlName,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            // Handle error
            const errorMessage = data.errorMessage;
            alert(errorMessage);
            // Prevent changes by reverting to the previous state
            setEditMode(!editMode);
            setUrlName("")

          });
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        // Handle network or server errors
        alert("An error occurred. Please try again later.");
        // Prevent changes by reverting to the previous state
        setEditMode(!editMode);
      });
  };

  const handleToggle = () => {
    setIsMetric(!isMetric);

    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        default_unit_of_measurement: isMetric ? "traditional" : "metric",
      }),
    }).then((response) => {
      if (!response.ok) {
        // Handle error
      }
    });
  };

  const handlePublicToggle = () => {
    if (!hasSilver) {
      alert("A Silver subscription is required for this feature");
      return;
    }
    
    setIsPublic(!isPublic);
    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser/public`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        public: isPublic ? 0 : 1,
      }),
    }).then((response) => {
      if (!response.ok) {
        // Handle error
      }
    });
  };

  const fetchUserMetricSetting = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getUser`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { user } = await response.json();

      if (!user) {
        // Handle error
      }
      setCurrentUser(user.id);
      setIsMetric(user.default_unit_of_measurement === "metric");
      setIsPublic(user.recipes_public);
      setDisplayName(user.display_name);
      setUrlName(user.public_url_name);
    } catch (error) {
      // Handle error
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/isLoggedIn`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, [id]);

  const [hasGold, setHasGold] = useState(false);
  const [hasSilver, setHasSilver] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/gold`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setHasGold(true);
          setHasSilver(true);
          console.log("Gold");
        } else if (response.status === 403) {
          console.log("Not authorized");
          setHasGold(false);
        } else {
          console.log("Error");
          setHasGold(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setHasGold(false);
      });
  }, [id]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/silver`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setHasSilver(true);
          console.log("Silver");
        } else if (response.status === 403) {
          console.log("Not authorized");
          setHasSilver(false);
        } else {
          console.log("Error");
          setHasSilver(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setHasSilver(false);
      });
  }, [id]);

  //Handle Form Changes
  const handleDisplayNameChange = (event) => {
    setDisplayName(event.target.value);
  };

  //Handle Form Changes
  const handleUrlNameChange = (event) => {
    const value = event.target.value;
    
    // Remove spaces and slashes from the input value
    const sanitizedValue = value.replace(/[^\w-]/g, '');
  
    setUrlName(sanitizedValue);
  };

  const fetchRecipeUsageStats = async () => {
    try {
      const params = new URLSearchParams();
      if (courseFilter !== "all") params.append("course", courseFilter);
      if (tagFilter !== "all") params.append("tag", tagFilter);
      if (minUsageFilter) params.append("minUsage", minUsageFilter);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/usage-stats?${params}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRecipeUsageStats(data);
        setAvailableCourses(data.availableCourses || []);
        setAvailableTags(data.availableTags || []);
      }
    } catch (error) {
      console.error("Error fetching recipe usage stats:", error);
    }
  };

  const toggleUsageStats = () => {
    if (!showUsageStats) {
      fetchRecipeUsageStats();
    }
    setShowUsageStats(!showUsageStats);
  };
  
  const handleFilterChange = () => {
    if (showUsageStats) {
      fetchRecipeUsageStats();
    }
  };
  
  const resetFilters = () => {
    setCourseFilter("all");
    setTagFilter("all");
    setMinUsageFilter("");
    if (showUsageStats) {
      fetchRecipeUsageStats();
    }
  };

  // Google Drive functions
  const checkGoogleAuth = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/google/status`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setGoogleConnected(data.connected);
        if (data.email) {
          setGoogleEmail(data.email);
        }
      }
    } catch (err) {
      console.error("Error checking Google auth status:", err);
    }
  };

  const handleConnectGoogle = () => {
    // Redirect to Google OAuth flow
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google/drive`;
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm("Are you sure you want to disconnect Google Drive? Your linked cookbooks will no longer be accessible.")) {
      return;
    }

    setDisconnecting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/google/disconnect`,
        {
          method: 'DELETE',
          credentials: "include"
        }
      );

      if (response.ok) {
        setGoogleConnected(false);
        setGoogleEmail("");
        alert("Google Drive disconnected successfully.");
      } else {
        alert("Failed to disconnect Google Drive. Please try again.");
      }
    } catch (err) {
      console.error("Error disconnecting Google Drive:", err);
      alert("Failed to disconnect Google Drive. Please try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  useEffect(() => {
    if (showUsageStats) {
      fetchRecipeUsageStats();
    }
  }, [courseFilter, tagFilter, minUsageFilter]);

  const sortRecipesByUsage = (order) => {
    setUsageSortOrder(order);
    if (recipeUsageStats) {
      const sorted = [...recipeUsageStats.recipes].sort((a, b) => {
        if (order === "desc") {
          return b.weekly_plan_count - a.weekly_plan_count;
        } else {
          return a.weekly_plan_count - b.weekly_plan_count;
        }
      });
      setRecipeUsageStats({ ...recipeUsageStats, recipes: sorted });
    }
  };

  useEffect(() => {
    // Fetch ingredients
    fetchUserMetricSetting();
    // Check Google Drive connection status
    checkGoogleAuth();
  }, []);

  return (
    <div>
      <div
        className="container rounded p-2 mb-4"
        style={{ backgroundColor: "#444444", color: "white" }}
      >
        <div className="col-sm-2">
          <div className="d-flex justify-content-center">
            <Form.Switch
              type="switch"
              id="custom-switch"
              label="Use Metric"
              checked={isMetric}
              onChange={handleToggle}
            />
          </div>
        </div>
      </div>

      <div className="col-sm-2">
      <OverlayTrigger
          delay={{ hide: 150, show: 150 }}
          overlay={(props) => (
            <Tooltip {...props}>
              Use to make the below public URL active for anyone to browse your recipes.
            </Tooltip>
          )}
          placement="bottom"
        >
          <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
        </OverlayTrigger>
        <div className="d-flex justify-content-left mb-2">
          <Form.Switch
            type="switch"
            id="custom-switch-public"
            label="Make Recipe's Public"
            checked={isPublic}
            onChange={handlePublicToggle}
          />                  
        </div>
        
      </div>

      <div className="col-sm-2">
        <div className="d-flex justify-content-left mb-2">
          {editMode ? (
            <Button variant="outline-primary" onClick={handleSaveChanges}>
              Save Changes
            </Button>
          ) : (
            <Button variant="outline-primary" onClick={handleEditToggle}>
              Enter Edit Mode
            </Button>
          )}
        </div>
      </div>

      {editMode ? (
        <div>
          <div className="row">
            <div className="col-sm-6">
              <div className="row">
                <div className="col-sm-6">
                  <label className="mr-2">
                  <OverlayTrigger
          delay={{ hide: 150, show: 150 }}
          overlay={(props) => (
            <Tooltip {...props}>
              The name that is displayed on your public page.
            </Tooltip>
          )}
          placement="bottom"
        >
          <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
        </OverlayTrigger>
                    <b>Display Name:</b>
                  </label>
                  <div className="d-inline-flex">
                    <input
                      type="text"
                      className="form-control"
                      value={displayName}
                      onChange={handleDisplayNameChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row my-2">
            <div className="col-sm-2">
            <OverlayTrigger
          delay={{ hide: 150, show: 150 }}
          overlay={(props) => (
            <Tooltip {...props}>
              Your own public page for your recipes.
            </Tooltip>
          )}
          placement="bottom"
        >
          <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
        </OverlayTrigger>
              <a className="dropdown-item" href="/meals"><b>Public URL:</b> </a>
            </div>
            <div className="row">
              <div className="col-sm-6">
                <label className="mr-2">platepassion.com/recipes/</label>
                <div className="d-inline-flex">
                  <input
                    type="text"
                    className="form-control"
                    value={urlName}
                    onChange={handleUrlNameChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {" "}
          <div className="row">
            <div className="col-sm-6">
              <div className="row">
                <div className="col-sm-6">
                  <label className="mr-2">
                  <OverlayTrigger
          delay={{ hide: 150, show: 150 }}
          overlay={(props) => (
            <Tooltip {...props}>
              The name that is displayed on your public page.
            </Tooltip>
          )}
          placement="bottom"
        >
          <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
        </OverlayTrigger>
                    <b>Display Name:</b>
                  </label>
                  <div className="row">
                    
              <div className="col-sm-6">
                
<p>{displayName}</p>
                  </div> </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row my-2">
            <div className="col-sm-2">
            <OverlayTrigger
          delay={{ hide: 150, show: 150 }}
          overlay={(props) => (
            <Tooltip {...props}>
              Your own public page for your recipes.
            </Tooltip>
          )}
          placement="bottom"
        >
          <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
        </OverlayTrigger>
              <b>Public URL:</b>
            </div>
            <div className="row">
              <div className="col-sm-6">
                <label className="mr-2">platepassion.com/recipes/{urlName}</label>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive Integration Section */}
      <div className="mt-4">
        <div
          className="container rounded p-3"
          style={{ backgroundColor: "#444444", color: "white" }}
        >
          <div className="mb-3">
            <h5 className="mb-3">
              <i className="bi bi-google me-2"></i>
              Google Drive Integration
            </h5>
            {googleConnected ? (
              <div>
                <p className="mb-2">
                  <strong>Status:</strong> <span className="text-success">Connected</span>
                </p>
                <p className="mb-3">
                  <strong>Account:</strong> {googleEmail}
                </p>
                <p className="text-muted small mb-3">
                  You can link cookbooks from your Google Drive without uploading them to our servers.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDisconnectGoogle}
                  disabled={disconnecting}
                >
                  {disconnecting ? "Disconnecting..." : "Disconnect Google Drive"}
                </Button>
              </div>
            ) : (
              <div>
                <p className="mb-3">
                  Connect your Google Drive to link PDF and ePub cookbooks directly from your Drive.
                </p>
                <p className="text-muted small mb-3">
                  Benefits:
                </p>
                <ul className="text-muted small mb-3">
                  <li>Access cookbooks without uploading to our servers</li>
                  <li>Save storage space</li>
                  <li>Keep files in your Google Drive</li>
                </ul>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleConnectGoogle}
                >
                  <i className="bi bi-google me-2"></i>
                  Connect Google Drive
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div
          className="container rounded p-3"
          style={{ backgroundColor: "#444444", color: "white" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faChartBar} className="me-2" />
              Recipe Usage Statistics
            </h5>
            <Button
              variant="outline-light"
              size="sm"
              onClick={toggleUsageStats}
            >
              {showUsageStats ? "Hide" : "Show"} Statistics
            </Button>
          </div>

          {showUsageStats && recipeUsageStats && (
            <>
              <div className="row mb-3">
                <div className="col-sm-6">
                  <p className="mb-1">
                    <strong>Total Recipes:</strong> {recipeUsageStats.totalRecipes}
                  </p>
                  <p className="mb-1">
                    <strong>Total Times Added to Weekly Plan:</strong> {recipeUsageStats.totalUsage}
                  </p>
                </div>
                <div className="col-sm-6 text-end">
                  <Button
                    variant={usageSortOrder === "desc" ? "primary" : "outline-light"}
                    size="sm"
                    className="me-2"
                    onClick={() => sortRecipesByUsage("desc")}
                  >
                    <FontAwesomeIcon icon={faSortAmountDown} /> Most Used
                  </Button>
                  <Button
                    variant={usageSortOrder === "asc" ? "primary" : "outline-light"}
                    size="sm"
                    onClick={() => sortRecipesByUsage("asc")}
                  >
                    <FontAwesomeIcon icon={faSortAmountUp} /> Least Used
                  </Button>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-3">
                  <label className="text-light mb-1">Filter by Course:</label>
                  <Form.Select 
                    size="sm"
                    value={courseFilter} 
                    onChange={(e) => setCourseFilter(e.target.value)}
                    className="bg-dark text-light"
                  >
                    <option value="all">All Courses</option>
                    {availableCourses.map(course => (
                      <option key={course} value={course}>{course}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-sm-3">
                  <label className="text-light mb-1">Filter by Tag:</label>
                  <Form.Select 
                    size="sm"
                    value={tagFilter} 
                    onChange={(e) => setTagFilter(e.target.value)}
                    className="bg-dark text-light"
                  >
                    <option value="all">All Tags</option>
                    {availableTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-sm-3">
                  <label className="text-light mb-1">Minimum Usage:</label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="1"
                    value={minUsageFilter}
                    onChange={(e) => setMinUsageFilter(e.target.value)}
                    placeholder="e.g., 5"
                    className="bg-dark text-light"
                  />
                </div>
                <div className="col-sm-3 d-flex align-items-end">
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={resetFilters}
                    className="w-100"
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {recipeUsageStats.recipes.length > 0 ? (
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Recipe Name</th>
                        <th>Course</th>
                        <th>Tags</th>
                        <th className="text-center">Times Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipeUsageStats.recipes.map((recipe) => (
                        <tr key={recipe.id}>
                          <td>{recipe.name}</td>
                          <td>{recipe.course || "N/A"}</td>
                          <td style={{ maxWidth: '200px' }}>
                            {recipe.tags ? (
                              <div>
                                {(() => {
                                  const tags = recipe.tags.split(',').map(tag => tag.trim());
                                  const displayLimit = 3;
                                  const hasMore = tags.length > displayLimit;
                                  
                                  return (
                                    <div className="d-flex flex-wrap gap-1">
                                      {tags.slice(0, displayLimit).map((tag, index) => (
                                        <span key={index} className="badge bg-secondary" style={{ fontSize: '0.75rem' }}>
                                          {tag}
                                        </span>
                                      ))}
                                      {hasMore && (
                                        <OverlayTrigger
                                          placement="bottom"
                                          overlay={
                                            <Tooltip>
                                              <div className="d-flex flex-wrap gap-1">
                                                {tags.slice(displayLimit).map((tag, index) => (
                                                  <span key={index} className="badge bg-light text-dark me-1 mb-1">
                                                    {tag}
                                                  </span>
                                                ))}
                                              </div>
                                            </Tooltip>
                                          }
                                        >
                                          <span className="badge bg-info" style={{ fontSize: '0.75rem', cursor: 'pointer' }}>
                                            +{tags.length - displayLimit} more
                                          </span>
                                        </OverlayTrigger>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : "N/A"}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary">
                              {recipe.weekly_plan_count}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      {(courseFilter !== "all" || tagFilter !== "all" || minUsageFilter) 
                        ? "No recipes match your current filters." 
                        : "No recipes have been added to weekly plans yet."}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { UserSettings };
